/**
 * Cloudflare Access identity helpers.
 *
 * `admin.lashpic.com` 은 Zero Trust(`maestiq` 팀)에 의해 edge에서 보호되므로,
 * 본 앱이 렌더되는 시점에는 이미 사용자 인증이 끝나 있다. 여기서는 단지
 * "현재 인증된 사용자"의 이메일/UUID를 읽어와 UI에 표시하는 용도로만 사용한다.
 *
 * 주요 레퍼런스:
 *   - CF Access Identity endpoint (per-app):
 *       GET /cdn-cgi/access/get-identity  (쿠키 `CF_Authorization` 필요)
 *       응답 JSON: { email, user_uuid, name, amr, iss, ... }
 *   - Logout endpoint:
 *       GET /cdn-cgi/access/logout  (또는 ${TEAM_DOMAIN}/cdn-cgi/access/logout)
 *
 * 주의:
 *   - 본 유틸은 "서버 사이드 권한 판정" 용도가 아니다 — 그건 apps/api Worker 가
 *     Cf-Access-Jwt-Assertion 헤더를 검증해서 수행한다. 여기는 UI 표시 전용.
 */

import { useEffect, useState } from 'react';

export interface CfAccessIdentity {
  /** 인증된 이메일 (CF Access Policy 가 이미 allowlist 검증함). */
  readonly email: string;
  /** Cloudflare 가 사용자에게 발급한 고유 UUID (세션 간 불변). */
  readonly user_uuid: string;
  /** 사용자 이름(제공되는 경우). */
  readonly name?: string;
  /** Authentication Method References — 예: ["oidc","webauthn","otp"]. */
  readonly amr?: readonly string[];
  /** JWT iss claim (CF Access issuer). */
  readonly iss?: string;
  /** JWT exp claim (epoch seconds). */
  readonly exp?: number;
}

/**
 * 브라우저에서 현재 CF Access 인증 정보를 조회.
 * - 성공: `CfAccessIdentity`
 * - 미인증/에러: `null`
 *
 * CF Access 로 보호되지 않은 환경(로컬 `vite dev` 등)에서도 안전하게 null 을 반환.
 */
export async function fetchCfAccessIdentity(): Promise<CfAccessIdentity | null> {
  try {
    const res = await fetch('/cdn-cgi/access/get-identity', {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<CfAccessIdentity>;
    if (!data.email || !data.user_uuid) return null;
    return {
      email: String(data.email),
      user_uuid: String(data.user_uuid),
      name: data.name ? String(data.name) : undefined,
      amr: Array.isArray(data.amr) ? (data.amr as string[]) : undefined,
      iss: data.iss ? String(data.iss) : undefined,
      exp: typeof data.exp === 'number' ? data.exp : undefined,
    };
  } catch {
    return null;
  }
}

export interface UseCfAccessIdentityResult {
  readonly identity: CfAccessIdentity | null;
  readonly isLoaded: boolean;
}

/**
 * React 훅 — 마운트 시 `/cdn-cgi/access/get-identity` 1회 조회.
 */
export function useCfAccessIdentity(): UseCfAccessIdentityResult {
  const [identity, setIdentity] = useState<CfAccessIdentity | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchCfAccessIdentity().then((value) => {
      if (cancelled) return;
      setIdentity(value);
      setIsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { identity, isLoaded };
}

/**
 * CF Access 로그아웃 URL 로 이동.
 * - Application-level: `/cdn-cgi/access/logout` (기본, 현재 탭만 로그아웃)
 * - Team-level: `${TEAM_DOMAIN}/cdn-cgi/access/logout` (SSO 전체 로그아웃)
 *
 * Admin 1인 운영 맥락에서는 application-level 로 충분하며, 기본값으로 사용.
 */
export function cfAccessLogout(): void {
  if (typeof window === 'undefined') return;
  window.location.assign('/cdn-cgi/access/logout');
}

/**
 * CF Access JWT 쿠키 조회(브라우저).
 *
 * - 기본 쿠키 이름은 `CF_Authorization` 이며, CF 가 `HttpOnly=false` 로 설정하므로 브라우저에서 접근 가능.
 * - 단, 환경에 따라 HttpOnly 가 활성화될 수 있으므로 **서버 쪽 검증에서는 Cf-Access-Jwt-Assertion
 *   헤더를 쓰는 것이 정석**. 본 함수는 프런트엔드에서 expire time 확인 같은 경우에만 쓰인다.
 */
export function getCfAccessJwtFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)CF_Authorization=([^;]+)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1] ?? '');
  } catch {
    return match[1] ?? null;
  }
}
